<script lang="tsx">

import {defineComponent, PropType} from "vue";

export const HeroTypeObj = {
  Hero1: 1,
  Hero2: 2,
  Hero3: 3,
  Hero4: 4,
  Hero5: 5,
  Hero6: 6
}

export type HeroType = keyof typeof HeroTypeObj;

export default defineComponent({
  name: "Hero",
  props: {
    type: {
      type: String as PropType<HeroType>,
      required: true
    },
    withFocusLine: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      required: false
    },
  },
  render() {
    return (
      <a id={`${typeof this.$props.id !== 'undefined' ? this.$props.id : ''}`}>
      <h1 class={`hero hero--${HeroTypeObj[this.$props.type]}`}>
        {this.$props.withFocusLine && <div class="hero-focusline"/>}

        <div class="hero-text">
          {this.$slots.topline && <div class="hero-topline">
            {this.$slots.topline()}
          </div>}

          {this.$slots.default && this.$slots.default()}

          {this.$slots.description && <div class="hero-description">
            {this.$slots.description()}
          </div>}
        </div>
      </h1>
      </a>
    )
  }
})
</script>


<style lang="scss" scoped>
@import "src/css/common/index";

@mixin focusline($width, $distance) {
  .hero-focusline {
    padding-right: $distance + $width;

    &::before {
      content: '';
      width: $width;
      height: 100%;

      position: absolute;
      left: 0;
      top: 0;

      background: $color-brand-focusline;
    }
  }
}

@include important {
  .hero {
    display: flex;
    align-items: center;
    position: relative;
    word-break: break-word;

    margin: 0;

    font-family: "SiemensSans", sans-serif;
    font-weight: 900;

    &--1 {
      @include focusline(6px, $spacing-4);
      font-size: 60px;
      line-height: 72px;
      letter-spacing: -0.5px;
      margin-bottom: $spacing-8;

      @media (max-width: $breakpoint-M) {
        @include focusline(5px, $spacing-3);
        font-size: 48px;
        line-height: 56px;
        margin-bottom: $spacing-7;
      }

      @media (max-width: $breakpoint-XS) {
        @include focusline(4px, $spacing-3);
        font-size: 40px;
        line-height: 48px;
      }
    }

    &--2 {
      @include focusline(4px, $spacing-3);
      font-size: 40px;
      line-height: 52px;
      letter-spacing: -0.5px;
      margin-bottom: $spacing-7;

      @media (max-width: $breakpoint-M) {
        @include focusline(4px, $spacing-3);
        font-size: 36px;
        line-height: 48px;
        margin-bottom: $spacing-6;
      }

      @media (max-width: $breakpoint-XS) {
        @include focusline(3px, $spacing-2);
        font-size: 32px;
        line-height: 40px;
      }
    }

    &--3 {
      @include focusline(4px, $spacing-3);
      font-size: 36px;
      line-height: 46px;
      letter-spacing: -0.5px;
      margin-bottom: $spacing-6;

      @media (max-width: $breakpoint-M) {
        @include focusline(3px, $spacing-2);
        font-size: 32px;
        line-height: 42px;
        margin-bottom: $spacing-6;
      }

      @media (max-width: $breakpoint-XS) {
        @include focusline(3px, $spacing-2);
        font-size: 28px;
        line-height: 38px;
        margin-bottom: $spacing-5;
      }
    }

    &--4 {
      @include focusline(3px, $spacing-2);
      font-size: 32px;
      line-height: 38px;
      margin-bottom: $spacing-5;

      @media (max-width: $breakpoint-M) {
        @include focusline(3px, $spacing-2);
        font-size: 28px;
        line-height: 34px;
        margin-bottom: $spacing-5;
      }

      @media (max-width: $breakpoint-XS) {
        @include focusline(3px, $spacing-2);
        font-size: 26px;
        line-height: 34px;
        margin-bottom: $spacing-4;
      }
    }

    &--5 {
      @include focusline(2px, $spacing-2);
      font-size: 24px;
      line-height: 32px;
      margin-bottom: $spacing-4;

      @media (max-width: $breakpoint-XS) {
        @include focusline(2px, $spacing-2);
        font-size: 22px;
        line-height: 30px;
        margin-bottom: $spacing-3;
      }
    }

    &--6 {
      @include focusline(2px, $spacing-2);
      font-size: 20px;
      line-height: 28px;
      margin-bottom: $spacing-1;
    }

    &-topline {
      font-weight: 900;
      font-size: 18px;
      line-height: 22px;
      letter-spacing: 1px;
      text-transform: uppercase;
      padding-bottom: $spacing-2;
    }

    &-description {
      font-weight: normal;
    }
  }
}
</style>


